import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { JobStatus } from '@aws-sdk/client-iot';
import { AuditAction, AuditType } from '@common/audit-log/models/AuditLog';
import { Columns, Config, DefaultConfig } from 'ngx-easy-table';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  merge,
  Observable,
  of,
} from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AuditService } from '../api/backend/services/audit/audit.service';
import { DeploymentsService } from '../api/backend/services/deployments/deployments.service';
import { DatabaseService } from '../api/database.service';
import { ThingTypesService } from '../api/thing-types.service';
import { UtilsService } from '../lib/utils.service';
import { ListDeployments } from '../models/backend/deployment/list-deployments';
import { Job } from '../models/job';
import { MetaVersionJob } from '../models/meta-version-job.model';
import { NotificationService } from '../shared/notification.service';

interface GlobalFilters {
  inProgressOnly: boolean;
  singlesExcluded: boolean;
}

@Component({
  selector: 'app-deployements',
  templateUrl: './deployements.component.html',
  styleUrls: ['./deployements.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeployementsComponent implements OnInit {
  private readonly DEFAULT_IN_PROGRESS_ONLY = false;
  private readonly DEFAULT_SINGLES_EXCLUDED = true;
  private readonly MAX_ITEMS_PER_REQUEST = 250; // Max items returned by request

  public jobs?: Job[];
  public jobs$?: Observable<Job[]>;

  public readonly columns: Columns[] = [
    { key: 'createdAtStr', title: 'Creation Date', width: '8%' },
    { key: 'jobId', title: 'Job name' },
    { key: 'metaversionId', title: 'Metaversion' },
    { key: 'singleOrMulti', title: 'Target', orderEnabled: false, width: '6%' },
    { key: 'thingType', title: 'Thing type', width: '6%' },
    { key: 'status', title: 'Status', width: '6%' },
    { key: 'createdBy', title: 'Creator', width: '6%' },
    {
      key: 'actions',
      title: 'Actions',
      orderEnabled: false,
      searchEnabled: false,
      width: '3%',
    },
  ];

  public configuration: Config = {
    ...DefaultConfig,
    searchEnabled: true,
    paginationEnabled: true,
    rows: 50,
    orderEnabled: true,
  };

  public thingTypes$?: Observable<string[]>;

  public formGroupGlobalFilters = new FormGroup({
    inProgressOnly: new FormControl<boolean>(false),
    singlesExcluded: new FormControl<boolean>(true),
  });

  public formGroupLocalFilters = new UntypedFormGroup({
    createdAtStr: new UntypedFormControl(),
    jobId: new UntypedFormControl(),
    metaversionId: new UntypedFormControl(),
    singleOrMulti: new UntypedFormControl(null),
    thingType: new UntypedFormControl(),
    status: new UntypedFormControl(),
    createdBy: new UntypedFormControl(),
  });

  public filterLoading = false;
  public errorLoading = false;
  public nextToken?: string;

  public loadMore$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );
  private globalFiltersChange$ = new BehaviorSubject<GlobalFilters>({
    inProgressOnly: this.DEFAULT_IN_PROGRESS_ONLY,
    singlesExcluded: this.DEFAULT_SINGLES_EXCLUDED,
  });

  private destroyRef = inject(DestroyRef);

  constructor(
    private readonly deploymentsService: DeploymentsService,
    private readonly databaseService: DatabaseService,
    private readonly utilsService: UtilsService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly notif: NotificationService,
    private readonly thingTypesService: ThingTypesService,
    private readonly auditService: AuditService,
  ) {}

  public ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    const inProgressFromParam = params?.inProgressOnly === 'true';
    const singlesExcludedFromParam = params?.singlesExcluded !== 'false';
    const newValues: GlobalFilters = {
      inProgressOnly:
        typeof params.inProgressOnly !== 'undefined'
          ? inProgressFromParam
          : this.DEFAULT_IN_PROGRESS_ONLY,
      singlesExcluded:
        typeof params.singlesExcluded !== 'undefined'
          ? singlesExcludedFromParam
          : this.DEFAULT_SINGLES_EXCLUDED,
    };
    // Only update if different from current form value.
    if (
      newValues.inProgressOnly !==
        this.formGroupGlobalFilters.value.inProgressOnly ||
      newValues.singlesExcluded !==
        this.formGroupGlobalFilters.value.singlesExcluded
    ) {
      this.formGroupGlobalFilters.setValue(newValues, { emitEvent: false });
      this.globalFiltersChange$.next(newValues);
    }

    this.formGroupGlobalFilters.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const values: GlobalFilters = {
          inProgressOnly:
            this.formGroupGlobalFilters.value.inProgressOnly ??
            this.DEFAULT_IN_PROGRESS_ONLY,
          singlesExcluded:
            this.formGroupGlobalFilters.value.singlesExcluded ??
            this.DEFAULT_SINGLES_EXCLUDED,
        };
        // Only update the router if the value differs from its default.
        const queryParams: Params = {
          inProgressOnly:
            values.inProgressOnly !== this.DEFAULT_IN_PROGRESS_ONLY
              ? values.inProgressOnly
              : null,
          singlesExcluded:
            values.singlesExcluded !== this.DEFAULT_SINGLES_EXCLUDED
              ? values.singlesExcluded
              : null,
        };
        // When a switch changes, reset nextToken and trigger a refetch.
        this.nextToken = undefined;
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
        this.globalFiltersChange$.next(values);
      });

    this.jobs$ = merge(this.globalFiltersChange$, this.loadMore$).pipe(
      switchMap((event) => {
        let filters: GlobalFilters;
        if (typeof event === 'boolean') {
          // "loadMore" event; use the latest global filters value.
          filters = this.globalFiltersChange$.value;
        } else {
          // Global filters changed: clear jobs.
          this.jobs = [];
          filters = event;
        }
        const status = filters.inProgressOnly
          ? ('IN_PROGRESS' as JobStatus)
          : undefined;
        this.filterLoading = true;
        return this.getListJobs$(status, this.nextToken);
      }),
      tap((jobsResponse: ListDeployments) => {
        this.nextToken = jobsResponse?.nextToken;
        this.filterLoading = false;
      }),
      map((jobsResponse) => this.parseAndSortJobs(jobsResponse)),
      switchMap((jobs) => this.getMetaversions$(jobs)),
      tap((jobs) => {
        let filteredJobs = jobs;
        if (this.formGroupGlobalFilters.value.singlesExcluded) {
          filteredJobs = jobs.filter((job) => job.singleOrMulti !== 'SINGLE');
        }

        this.jobs = this.jobs ? [...this.jobs, ...filteredJobs] : filteredJobs;

        if (
          this.nextToken &&
          filteredJobs.length < this.MAX_ITEMS_PER_REQUEST &&
          this.jobs.length < this.MAX_ITEMS_PER_REQUEST
        ) {
          // frontend filter gave too few results, the page will feel "empty" or wrongly loaded
          // we need to trigger loading more results to avoid this
          // it will loop until at least 250 (MAX_ITEMS_PER_REQUEST) results are loaded or no nextToken is available
          this.loadMore();
        }
      }),
      shareReplay(1),
    );

    this.thingTypes$ = this.thingTypesService
      .getThingTypes()
      .pipe(shareReplay(1));
  }

  /**
   * Allows IDEs to correctly infer the object type to provide completion and checks in HTML template
   */
  public typedJobObject(job: Job): Job {
    return job;
  }

  public resetFilters(): void {
    this.formGroupLocalFilters.reset();
    this.formGroupGlobalFilters.setValue({
      inProgressOnly: this.DEFAULT_IN_PROGRESS_ONLY,
      singlesExcluded: this.DEFAULT_SINGLES_EXCLUDED,
    });
  }

  private getMetaversions$(jobs: Job[]): Observable<Job[]> {
    const resourceIds = jobs.map((j) => j.jobId);
    return combineLatest([
      this.auditService.getLatestEvent(
        AuditType.DEPLOYMENT,
        AuditAction.START,
        resourceIds,
      ),
      this.databaseService.getMetaversionForJobs(resourceIds),
    ]).pipe(
      map(([logs, metaversions]) => {
        // "related jobs" are splitted jobs. Only the first ("main") one is useful
        const excludedJobs = new Set<string>();
        const mapByJobId: Record<string, MetaVersionJob> = {};
        metaversions.forEach((mv) => {
          mapByJobId[mv.jobId] = mv;
          if (mv.relatedJobs) {
            mv.relatedJobs.forEach((jobId) => excludedJobs.add(jobId));
          }
        });

        return jobs
          .filter((job) => !excludedJobs.has(job.jobId))
          .map((job) => {
            const metaVersionJob = mapByJobId[job.jobId];
            return job
              .addMetaversionId(metaVersionJob?.metaversionId)
              .addSingleOrMulti(metaVersionJob?.jobType)
              .addCreatedBy(logs?.[job.jobId]?.userId)
              .addVersionFilterCounter(metaVersionJob?.targetedVersions)
              .addProgressiveRolloutConfig(metaVersionJob?.progressiveRollout);
          });
      }),
      catchError((err: Error) => {
        this.notif.showError(
          err?.message ??
            'An error occurred while fetching the deployment/metaversion associations',
          err,
        );
        return of(jobs);
      }),
    );
  }

  private parseAndSortJobs(jobsResponse: ListDeployments): Job[] {
    return Job.fromItemList(jobsResponse?.jobs ?? []).sort((a, b) =>
      this.utilsService.compare(a.createdAt, b.createdAt, false),
    );
  }

  private getListJobs$(
    status?: JobStatus,
    nextToken?: string,
  ): Observable<ListDeployments> {
    return this.deploymentsService.listDeployments(status, nextToken).pipe(
      catchError((err: Error) => {
        this.notif.showError(err.message, err);
        this.errorLoading = true;
        this.filterLoading = false;
        return of({
          jobs: [],
        }) as Observable<ListDeployments>;
      }),
    );
  }

  public loadMore(): void {
    this.loadMore$.next(true);
  }
}
